import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { ConfirmDialog } from "../components/ConfirmDialog";

interface Artifact {
  id: string;
  createdAt: string;
  path: string;
  sizeBytes: number;
  sha256: string;
  state: "built" | "delivered" | "failed" | "superseded";
  deliveredToEndpointId?: string;
  sourceIds?: string[];
  sourceNames?: string[];
}

type StateFilter = "all" | Artifact["state"];

const STATE_OPTIONS: { value: StateFilter; label: string }[] = [
  { value: "all", label: "All states" },
  { value: "built", label: "Built" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "superseded", label: "Superseded" },
];

export function ArtifactsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [confirmDelete, setConfirmDelete] = useState<Artifact | null>(null);

  const artifacts = useQuery({
    queryKey: ["artifacts"],
    queryFn: () => api.get<Artifact[]>("/api/v1/artifacts"),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/artifacts/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["artifacts"] }),
  });

  const allArtifacts = artifacts.data ?? [];

  const filtered = allArtifacts.filter((a) => {
    const matchesState = stateFilter === "all" || a.state === stateFilter;
    const term = search.toLowerCase();
    const matchesSearch =
      term === "" ||
      a.sourceNames?.some((n) => n.toLowerCase().includes(term)) ||
      a.id.toLowerCase().includes(term) ||
      a.sha256.toLowerCase().includes(term);
    return matchesState && matchesSearch;
  });

  const hasData = allArtifacts.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1200px] px-10 pb-16">
      <header className="pt-12">
        <div className="eyebrow">Cargo</div>
        <h1 className="mt-3 font-display text-[40px] font-semibold leading-tight tracking-tightest text-ink">
          Artifacts
        </h1>
        <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-ash">
          Each successful parse/scan seals metadata into a signed,{" "}
          <code className="rounded bg-paper px-1.5 py-0.5 font-mono text-[11.5px]">
            .lca
          </code>{" "}
          envelope encrypted with the Catalog's age public key. For air-gapped
          plants, download here and drop into the Catalog's Central UI for
          artifact upload.
        </p>
      </header>

      {hasData && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by source name, ID, or hash…"
            className="input flex-1 max-w-sm"
          />
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            className="input w-auto"
          >
            {STATE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <section className="mt-4 card overflow-hidden">
        {artifacts.isLoading ? (
          <p className="px-6 py-8 text-sm text-ash">Loading…</p>
        ) : !hasData ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-ash">
              No artifacts built yet. Run a 'Refresh' from the Sources page to
              produce one.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-rule bg-paper text-left">
              <tr>
                <Th>Built</Th>
                <Th>Source</Th>
                <Th>Size</Th>
                <Th>SHA-256</Th>
                <Th>State</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-8 text-center text-sm text-ash"
                  >
                    No artifacts match your search
                    {stateFilter !== "all" ? ` in state "${stateFilter}"` : ""}.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a.id} className="transition-colors hover:bg-paper">
                    <Td>
                      <div className="text-ink">
                        {new Date(a.createdAt).toLocaleString()}
                      </div>
                      <div className="font-mono text-[10.5px] text-ash">
                        {a.id.slice(0, 8)}…
                      </div>
                    </Td>
                    <Td>
                      {a.sourceNames && a.sourceNames.length > 1 ? (
                        <span
                          className="cursor-default text-ink"
                          title={a.sourceNames.join(", ")}
                        >
                          {a.sourceNames.length} sources
                        </span>
                      ) : a.sourceNames && a.sourceNames.length === 1 ? (
                        <span className="text-ink">{a.sourceNames[0]}</span>
                      ) : (
                        <span className="text-ash">—</span>
                      )}
                    </Td>
                    <Td>
                      <span className="font-mono text-[12px] text-ink">
                        {formatBytes(a.sizeBytes)}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono text-[11.5px] text-ash">
                        {a.sha256.slice(0, 16)}…
                      </span>
                    </Td>
                    <Td>
                      <StateBadge state={a.state} />
                    </Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`/api/v1/artifacts/${encodeURIComponent(a.id)}/download`}
                          download
                          className="btn-ghost text-[12px]"
                        >
                          ↓ .lca
                        </a>
                        <button
                          onClick={() => setConfirmDelete(a)}
                          disabled={remove.isPending}
                          className="btn-danger text-[12px]"
                        >
                          Delete
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </section>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete artifact"
        description="Delete this artifact? The .lca file will be removed from disk."
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function StateBadge({ state }: { state: Artifact["state"] }) {
  if (state === "delivered")
    return <span className="chip-emerald">Delivered</span>;
  if (state === "built") return <span className="chip-neutral">Built</span>;
  if (state === "failed") return <span className="chip-rose">Failed</span>;
  return <span className="chip-amber">Superseded</span>;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={
        "px-5 py-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash " +
        (className ?? "")
      }
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <td className={"px-5 py-4 align-top " + (className ?? "")}>{children}</td>
  );
}
